<template>
  <div
    ref="column"
    :class="[
      $.column,
      'board_column',
      taskDragging && $.task_drag_active,
      isCollapsed && $.collapsed,
      taskSettingsInfo && $.task_settings_opened,
      isCollapsed && 'column_drag_handle'
    ]"
    @click="columnClickHandler"
  >
    <div :class="[$.header, !isCollapsed && 'column_drag_handle']">
      <div :class="$.info">
        <div :class="$.main">
          <vgp-avatar
            v-if="column.picture"
            :class="$.avatar"
            :src="column.picture"
            size="small"
          />
          <svg-sprite
            v-if="column.id === 'empty' && groupingFieldInfo.key === 'resources' && !groupingFieldInfo.isCustom"
            type="regular"
            :size="24"
            name="assigned"
            color="#333333"
            :style="{ minWidth: '24px' }"
          />
          <div
            :class="[$.label, column.headerColor && $.colored]"
            :style="{
              backgroundColor: column.headerColor
            }"
          >
            <span
              v-if="!column.noLabel"
              :style="{
                color: column.headerLabelColor || ''
              }"
            >
              {{ column.value.toUpperCase() }}
            </span>
          </div>
        </div>
        <div :class="$.counter">
          {{ allTasks.length }}
        </div>
      </div>
      <div
        :class="$.buttons"
      >
        <iconButton
          v-if="showCollapse"
          v-tippy="{ content: isCollapsed ? locale('board_column_expand') : locale('board_column_collapse'), duration: [300, 0], delay: [200, 0] }"
          :icon="{ name: isCollapsed ? 'expand-4' : 'collaps-2', type: 'regular', size: 20 }"
          @onClick="collapseToggle"
        />
        <iconButton
          v-show="showCreate"
          v-tippy="{ content: locale('board_task_create'), duration: [300, 0], delay: [200, 0] }"
          :icon="{ name: 'plus-1', type: 'regular', size: 20 }"
          @onClick="newTask"
        />
        <iconButton
          v-if="showMore"
          :icon="{ name: 'more-1', type: 'regular', size: 20 }"
          @onClick="(e) => toggleContextMenu(column, 'column', 'main', e.target)"
        />
      </div>
    </div>
    <div
      ref="cardsScrollWrapper"
      :class="[
        $.column_content,
        'gantt_scroll',
        showColumnShadow ? $.shadowScroll : null
      ]"
    >
      <draggable
        ref="cardsDndContainer"
        :group="{ name: 'cards', put: canDragTasks ? ['cards'] : false }"
        :class="[
          'cards_dnd_container',
          $.cards,
          isCollapsed ? $.collapsed_column : '',
          taskDragging ? $.drag_accept : ''
        ]"
        :value="visibleTasks"
        :style="{
          paddingTop: `${paddingTop}px`,
          paddingBottom: `${paddingBottom}px`
        }"
        :data-column-id="column.id"
        v-bind="{
          animation: 200,
          group: 'cards',
          disabled: taskCardEditing || taskSettingsInfo,
          draggable: '.' + $.card_wrapper,
          ghostClass: $.drop_preview,
          forceFallback: true,
          fallbackClass: 'card_drag',
          fallbackOnBody: true,
          fallbackTolerance: 5,
          easing: 'cubic-bezier(1, 0, 0, 1)',
          dataIdAttr: 'data-id',
          emptyInsertThreshold: 0,
          scrollSensitivity: 200,
          scrollSpeed: 25
        }"
        :move="onTaskMove"
        @start="onTaskDragStart"
        @end="onTaskDrop"
      >
        <div
          v-for="task in visibleTasks"
          :key="`${column.id}:${task.id}`"
          :class="$.card_wrapper"
          :data-id="task.id"
        >
          <card
            v-if="tasksMap[task.id]"
            :column-id="column.id"
            :task="tasksMap[task.id]"
            :projectResources="projectResources"
            @taskChange="(e) => $emit('taskChange', e)"
          />
        </div>
        <template
          v-for="draftTask in draftTasks"
        >
          <div
            v-if="draftTask.columnId === column.id"
            :class="$.new_card_wrapper"
            :key="`${column.id}:${draftTask.id}`"
          >
            <card
              :column-id="column.id"
              :task="draftTask"
              :projectResources="projectResources"
            />
          </div>
        </template>
      </draggable>
      <div :class="$.bottom_part" @mouseover="mouseOver">
        <div
          v-if="emptyTask"
          ref="newCardWrapper"
          :class="$.new_card_wrapper"
          :key="`${column.id}:create`"
        >
          <card
            :column-id="column.id"
            :task="{ id: 'create' }"
            @createTask="createTask"
          />
        </div>
        <iconButton
          v-show="showCreate"
          :class="$.add_task_button"
          :icon="{ name: 'plus-1', type: 'regular', size: 20 }"
          :label="locale('board_task_create')"
          size="middle"
          @onClick="newTask"
        />
      </div>
    </div>
  </div>
</template>

<script>
import draggable from 'vuedraggable';
import { mapGetters } from 'vuex';

import Card from './Card.vue';
import iconButton from '$$vueCmp/globalButton/withIcon/iconButton.vue';

import { debounce } from '../../../helpers/boardHelpers';
import rights from '../../../components/rights';
import { locale } from 'moment';

export default {
  name: 'Column',
  components: {
    draggable,
    Card,
    iconButton,
  },
  inject: [
    'getTasksMap',
    'toggleContextMenu',
    'hideFilterPopup',
    'hideProjectSettingsPopup',
    'closeContextMenus'
  ],
  props: {
    groupingFieldInfo: {
      type: Object,
      required: true,
    },
    column: {
      type: Object,
      required: true,
      default: {},
    },
    isCollapsed: {
      type: Boolean,
      required: true,
      default: false,
    },
    order: {
      type: Array, // [<column index (position - 1)>, <columns count>]
      required: true,
      default: [1, 1],
    },
    acceptDragItem: {
      type: Boolean,
      default: true,
    },
    taskDragging: {
      type: Boolean,
      default: false,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    canDragTasks: {
      type: Boolean,
      default: false
    },
    tasksMap: {
      type: Object,
      default: {}
    },
    projectResources: {
      type: Object,
      default: {}
    },
    boardHeight: {
      type: Number,
      default: 1500
    },
    draftTasks: {
      type: Array,
      default: []
    }
  },
  data() {
    return {
      locale: __,
      emptyTask: false,
      shadowTop: false,
      scrollTop: 0,
      showColumnShadow: false,
      dragStartScrollTop: null,
      dragInfo: null,
      firstVisibleIndex: null,
    };
  },
  mounted() {
    window.columnComponent = this;
    this.$refs.cardsScrollWrapper.addEventListener('scroll', this.onScroll);
  },
  beforeDestroy() {
    this.$refs.cardsScrollWrapper.removeEventListener('scroll', this.onScroll);
  },
  computed: {
    isCustomField() {
      return !!this.groupingFieldInfo.isCustom;
    },

    columnShadow() {
      if (this.$refs.cardsScrollWrapper.scrollHeight > this.$refs.cardsScrollWrapper.getBoundingClientRect().height) {
        alert('You must be a member');
      }
    },

    contextMenuItems() {
      return {
        columnOrder: new Array(this.order[1]).fill(1).map((item, index) => ({
          name: `${index + 1} ${__('board_column_position')}`,
          id: `board_column_position_${index + 1}`,
          value: index,
          gpTestId: `gp_autotest_${index + 1}_board_column_position`,
          selected: this.order[0] === index,
        })),
      };
    },
    allTasks() {
      return [...this.column.tasks];
    },
    visibleTasks() {
      if (this.isCollapsed) return [];
      let topEdge = this.scrollTop;
      let bottomEdge = this.scrollTop;

      if (typeof this.dragStartScrollTop === 'number') {
        if (this.dragStartScrollTop < this.scrollTop) {
          topEdge = this.dragStartScrollTop;
        } else {
          bottomEdge = this.dragStartScrollTop;
        }
      }

      let firstVisibleIndex = -1;

      const visibleTasks = [...this.allTasks].filter((task, i) => {
        const filterCondition = task.offsetTop > topEdge - 600 && task.offsetTop < (bottomEdge + 600 + this.boardHeight); 

        if (filterCondition && firstVisibleIndex === -1) {
          firstVisibleIndex = i;
        }

        return filterCondition;
      });

      this.firstVisibleIndex = firstVisibleIndex;

      return visibleTasks;
    },
    paddingTop() {
      if (this.isCollapsed) return 0;

      return this.visibleTasks[0]?.offsetTop || 0;
    },
    paddingBottom() {
      if (this.isCollapsed) return 0;

      const lastVisible = this.visibleTasks.at(-1);

      return this.column.totalHeight - ((lastVisible?.offsetTop || 0) + (lastVisible?.taskHeight || 0));
    },
    onScroll() {
      return debounce((e) => {
        if (e.target.scrollTop > 50 && e.target.scrollTop < this.column.totalHeight - e.target.clientHeight - 50) {
          this.showColumnShadow = true;
        } else {
          this.showColumnShadow = false;
        }
        this.scrollTop = e.target.scrollTop;
      }, 5);
    },
    canCreateTasks() {
      return rights.project.hasRight(this.projectId, 'create_task');
    },
    showCollapse() {
      return !(this.emptyTask || (this.taskSettingsInfo && !this.isCollapsed));
    },
    showCreate() {
      return !this.isCollapsed && this.canCreateTasks && !(this.emptyTask || this.taskSettingsInfo);
    },
    showMore() {
      return !(this.isCollapsed || this.taskSettingsInfo);
    },
    ...mapGetters('board', ['allFields', 'taskCardEditing', 'taskSettingsInfo', 'groupingFieldInfo', 'projectId']),
  },
  watch: {
    taskDragging(newValue) {
      if (newValue && this.emptyTask) {
        this.emptyTask = false;
        this.scrollColumnTo({ bottom: true });
      }
    },
    draftTasks(newValue) {
      if (newValue.length && newValue.find(task => task.columnId === this.column.id)) {
        this.scrollColumnTo({ bottom: true });
      }
    }
  },
  methods: {
    onTaskDragStart(e) {
      this.hideFilterPopup();
      this.hideProjectSettingsPopup();
      this.closeContextMenus();

      if (!this.canDragTasks) {
        this.$toast.info(__('board_permission_restriction'), { timeout: 5000 });
      }

      this.dragStartScrollTop = this.$refs.cardsScrollWrapper.scrollTop;
      this.dragInfo = {
        dragItem: e.item._underlying_vm_,
        columnFrom: this.column.id,
        indexFrom: this.firstVisibleIndex + e.oldIndex,
      };
      this.$emit('taskDragStart', {
        task: e.item._underlying_vm_,
        columnFrom: this.column.id,
      });
    },
    onTaskDrop(e) {
      const { dragItem, columnFrom, indexFrom } = this.dragInfo;
      const toColumnVue = e.from === e.to ? this : e.to.__vue__.$parent;
      const columnTo = toColumnVue.column.id;
      const indexTo = toColumnVue.firstVisibleIndex + e.newIndex;

      this.$emit('onTaskDrop', {
        dragItem,
        columnFrom,
        indexFrom,
        columnTo,
        indexTo,
      });

      setTimeout(() => {
        this.dragStartScrollTop = null;
        this.dragInfo = null;
      }, 0);
    },
    mouseOver(e) {
      if (!this.taskDragging) return;

      this.$refs.cardsDndContainer._sortable._onDragOver({
        clientX: e.clientX,
        clientY: e.clientY,
        target: this.$refs.cardsDndContainer.$el,
        rootEl: this.$refs.cardsDndContainer.$el
      });
    },
    collapseToggle() {
      this.$emit('onCollapse', this.column.id);
    },
    columnClickHandler(e) {
      if (!this.isCollapsed || this.dragInfo || e.target.closest(`.${this.$.buttons}`) || this.taskSettingsInfo) return;

      this.collapseToggle();
    },
    newTask() {
      this.emptyTask = true;
      this.$nextTick(() => {
        this.scrollColumnTo({ bottom: true });
        this.$refs.newCardWrapper.querySelector('textarea').focus();
      });
    },
    createTask(name, isOutsideClick) {
      if (!name && isOutsideClick) {
        this.emptyTask = false;
        this.scrollColumnTo({ bottom: true });
        return;
      }

      this.$emit('createTask', name, this.column.id, this);
    },
    scrollColumnTo({ bottom, top, y }) {
      const pos = bottom ? this.$refs.cardsScrollWrapper.scrollHeight : top ? 0 : y;
      this.$refs.cardsScrollWrapper.scrollTo(0, pos); 
    }
  },
};
</script>

<style module="$" src="../less/column.less"></style>
